import type { IAdvertising } from '@sravni/types/lib/advertising';
import type { ILocationV2 } from '@sravni/types/lib/locations';
import EAdvertisingDialog from '@sravni/vitrina/lib/enums/EAdvertisingDialog';
import type IProduct from '@sravni/vitrina/lib/types/IProduct';

import { MONETIZATION_TYPES } from '../../constants/advertising';
import OfferTypes from '../../enums/offerTypes';
import ProductTypes from '../../enums/productTypes';

import { makeAdGEvent, makeMainGEvent, pushEvent } from './analytics';

const EVENT_CATEGORY = 'Page';

const SPECIFIC_LEASING_ORGANIZATIONS = [
  '65a7f417ccc6a0002c63548a',
  '65a6936be9db30002774056e',
  '5bb4f767245bc22a520a60fb',
];

interface IItemLabelFactory {
  product: IProduct;
  paymentNetwork: string;
}

export const itemLabelFactory = ({ product, paymentNetwork }: IItemLabelFactory): string =>
  `${product.organization.name.short}|${product.subName || product.name}${
    product.organization.id ? `|${product.organization.id}` : ''
  }${product.id ? `|${product.id}` : ''}${paymentNetwork ? `|${paymentNetwork}` : ''}`;

export const sendSponsorClickEvent = (organizationName: string, eventCategory: string): void => {
  pushEvent(
    makeMainGEvent({
      eventCategory: eventCategory || EVENT_CATEGORY,
      eventAction: 'Спонсор раздела',
      eventLabel: organizationName,
    }),
  );
};

export const sendLocationChangeEvent =
  (eventCategory: string) =>
  (newLocation: ILocationV2): void => {
    pushEvent(
      makeAdGEvent({
        eventCategory: eventCategory || EVENT_CATEGORY,
        eventAction: ['Фильтр', 'Ваш город'],
        eventLabel: newLocation.name,
      }),
    );
  };

function getFreeEventAction(offerType: OfferTypes, configName?: string, organizationId?: string) {
  if (configName === 'biznes-leasing' && SPECIFIC_LEASING_ORGANIZATIONS.includes(organizationId)) {
    return 'Переход к оформлению заявки';
  }

  switch (offerType) {
    case OfferTypes.SimilarProducts:
      return 'Бесплатный клик|Похожие';
    case OfferTypes.TopOffer:
      return 'Бесплатный клик|SEO-таблица ';
    default:
      return 'Бесплатный клик';
  }
}

export const sendFreeButtonClickEvent = (
  product: IProduct,
  eventCategory: string,
  paymentNetwork: string,
  offerType = OfferTypes.Default,
  configName?: string,
): void => {
  pushEvent(
    makeMainGEvent({
      eventCategory: eventCategory || EVENT_CATEGORY,
      eventAction: getFreeEventAction(offerType, configName, product.organization.id),
      eventLabel: itemLabelFactory({ product, paymentNetwork }),
    }),
  );
};

export const sendSelectionClickEvent = (
  product: IProduct,
  eventCategory: string,
  eventAction: string,
  paymentNetwork: string,
): void => {
  pushEvent(
    makeMainGEvent({
      eventCategory: eventCategory || EVENT_CATEGORY,
      eventAction,
      eventLabel: itemLabelFactory({ product, paymentNetwork }),
    }),
  );
};

export const sendFormButtonClick = ({
  eventCategory,
  eventAction,
  eventLabel,
  eventValue,
}: {
  eventCategory: string;
  eventAction: string;
  eventLabel?: string;
  eventValue?: number;
}): void => {
  pushEvent(
    makeMainGEvent({
      eventCategory,
      eventAction,
      eventLabel,
      eventValue,
    }),
  );
};

interface IAdvertisingButtonClickEventParams {
  product: IProduct;
  advertising: IAdvertising;
  offerType?: OfferTypes;
}

const REQUESTS_MONETIZATIONS = new Set([
  MONETIZATION_TYPES.PROMO_CODE,
  MONETIZATION_TYPES.REQUEST_LONG,
  MONETIZATION_TYPES.REQUEST_SHORT,
  MONETIZATION_TYPES.APPROVAL,
]);

function getCost(advertising: IAdvertising) {
  const notRequestDialogs = [
    EAdvertisingDialog.Application,
    EAdvertisingDialog.AutomaticApplication,
    EAdvertisingDialog.CacheBackDialog,
    EAdvertisingDialog.PhoneNumberDialog,
  ];

  const allAdvertisingDialogs = Object.values(EAdvertisingDialog);
  const advertisingDialogs: string[] = allAdvertisingDialogs.filter((dialog) => !notRequestDialogs.includes(dialog));

  if (!advertisingDialogs.includes(advertising?.dialog)) {
    return advertising?.cost;
  }
  return 0;
}

function getEventAction(advertising: IAdvertising, offerType: OfferTypes): string {
  if (!advertising) {
    return null;
  }

  const {
    monetization: { kind },
  } = advertising;

  return [getByKind(kind, offerType), getByOfferType(offerType)].filter(Boolean).join('|');
}

function getByKind(kind: string, offerType: OfferTypes) {
  if (REQUESTS_MONETIZATIONS.has(kind)) {
    return 'Переход к оформлению заявки';
  }

  switch (offerType) {
    case OfferTypes.Landing: {
      return 'Перейти на сайт';
    }
    case OfferTypes.Cashback: {
      return 'Отправить заявку на кэшбек';
    }
    default: {
      return 'Платный клик';
    }
  }
}

function getByOfferType(offerType: OfferTypes) {
  switch (offerType) {
    case OfferTypes.SimilarProducts:
      return 'Похожие';
    case OfferTypes.TopOffer:
      return 'SEO-таблица';
    case OfferTypes.BestOffer:
      return 'Популярное';
    default:
      return null;
  }
}

const getByProductType = (productType: string) => {
  switch (productType) {
    case ProductTypes.Credits:
      return 'Подробнее|Переход в Подбор кредита';
    default:
      return 'Подробнее|Переход к оформлению заявки';
  }
};

export const sendAdvertisingButtonClickEvent = (
  { product, advertising, offerType = OfferTypes.Default }: IAdvertisingButtonClickEventParams,
  eventCategory: string,
  paymentNetwork: string,
): void => {
  pushEvent(
    makeMainGEvent({
      eventCategory: eventCategory || EVENT_CATEGORY,
      eventAction: getEventAction(advertising, offerType),
      eventLabel: itemLabelFactory({ product, paymentNetwork }),
      eventValue: offerType === OfferTypes.Landing ? undefined : advertising?.cost,
    }),
  );
};

interface IComparisonButtonClickEventParams {
  product: IProduct;
  eventAction: string;
  eventCategory: string;
  paymentNetwork: string;
}

export const sendComparisonButtonClickEvent = ({
  product,
  eventCategory,
  eventAction,
  paymentNetwork,
}: IComparisonButtonClickEventParams): void => {
  pushEvent(
    makeMainGEvent({
      eventCategory: eventCategory || EVENT_CATEGORY,
      eventAction,
      eventLabel: itemLabelFactory({ product, paymentNetwork }),
    }),
  );
};

interface IComparisonPageClickEventParams {
  eventLabel: string;
  eventCategory: string;
}

export const sendToComparisonPageClickEvent = ({
  eventLabel,
  eventCategory,
}: IComparisonPageClickEventParams): void => {
  pushEvent(
    makeMainGEvent({
      eventCategory: eventCategory || EVENT_CATEGORY,
      eventAction: 'Переход к сравнению',
      eventLabel,
    }),
  );
};

export const sendApplicationProcessingEvent = (
  product: IProduct,
  eventCategory: string,
  paymentNetwork: string,
): void => {
  pushEvent(
    makeMainGEvent({
      eventCategory: eventCategory || EVENT_CATEGORY,
      eventAction: 'Переход к оформлению заявки',
      eventLabel: itemLabelFactory({ product, paymentNetwork }),
      eventValue: getCost(product?.advertising),
    }),
  );
};

export const sendFilterEvent = (label: string, values: string, eventCategory: string): void => {
  pushEvent(
    makeAdGEvent({
      eventCategory: eventCategory || EVENT_CATEGORY,
      eventAction: ['Фильтр', label],
      eventLabel: values,
    }),
  );
};

export const sendPaginationEvent = (eventCategory: string): void => {
  pushEvent(
    makeAdGEvent({
      eventCategory: eventCategory || EVENT_CATEGORY,
      eventAction: 'Показать еще',
    }),
  );
};

export const sendAdvancedShowEvent = (product: IProduct, eventCategory: string, paymentNetwork: string): void => {
  pushEvent(
    makeMainGEvent({
      eventCategory: eventCategory || EVENT_CATEGORY,
      eventAction: 'Подробнее',
      eventLabel: itemLabelFactory({ product, paymentNetwork }),
    }),
  );
};

export const sendGroupOpenEvent = (product: IProduct, eventCategory: string, paymentNetwork: string): void => {
  pushEvent(
    makeMainGEvent({
      eventCategory: eventCategory || EVENT_CATEGORY,
      eventAction: 'Еще предложения',
      eventLabel: itemLabelFactory({ product, paymentNetwork }),
    }),
  );
};

export const sendExtraFiltersOpenEvent = (): void => {
  pushEvent(
    makeAdGEvent({
      eventCategory: EVENT_CATEGORY,
      eventAction: 'Еще условия',
    }),
  );
};

export const sendRowClickEvent = (eventCategory: string, eventLabel: string, productType: string): void => {
  pushEvent(
    makeAdGEvent({
      eventCategory: eventCategory || EVENT_CATEGORY,
      eventAction: getByProductType(productType),
      eventLabel,
    }),
  );
};

export const sendEvent = (
  eventCategory: string,
  eventAction: string,
  eventLabel = '',
  eventValue: number | undefined = undefined,
): void => {
  pushEvent(
    makeMainGEvent({
      eventCategory,
      eventAction,
      eventLabel,
      eventValue,
    }),
  );
};

export const sendBookMakerCardTransitionEvt = (product: IProduct, eventCategory: string): void => {
  pushEvent(
    makeMainGEvent({
      eventCategory: eventCategory || 'Букмекеры',
      eventAction: 'Переход в карточку букмекера',
      eventLabel: product.organization?.name?.short,
      eventValue: getCost(product?.advertising),
    }),
  );
};

export const sendCreditCalculatorTypeEvent = (label: string): void => {
  pushEvent(
    makeMainGEvent({
      eventCategory: 'Кредиты',
      eventAction: 'Тип платежа',
      eventLabel: label,
      eventValue: undefined,
    }),
  );
};
